import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  MessageCircle,
  Mail,
  Send,
  Loader2,
  CheckCircle2,
  Eye,
  Users,
  Zap,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { NadiaAvatar } from "@/components/nadia/NadiaAvatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  getWhatsAppCohort,
  sendWhatsAppRecall,
  generateWeeklyReport,
} from "@/lib/api";

import { useCreditGate } from "@/components/nadia/CreditGate";
import { usePlanFeatures, ProFeatureNotice } from "@/components/nadia/PlanFeatures";

export default function Automazioni() {
  const planAccess = usePlanFeatures();
  const { gate, dialog: creditDialog } = useCreditGate();
  const [cohort, setCohort] = useState({ mode: "mock", cohort: [] });
  const [sendingWA, setSendingWA] = useState(false);
  const [waResults, setWaResults] = useState(null);

  const [email, setEmail] = useState("");
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    getWhatsAppCohort().then(setCohort).catch(() => setCohort({ mode: "mock", cohort: [] }));
  }, []);

  const doRecall = async (opId) => {
    setSendingWA(true);
    try {
      const res = await sendWhatsAppRecall({ recipients: cohort.cohort.map(({name, phone}) => ({name, phone})) }, opId);
      setWaResults(res);
      toast.success(
        res.mode === "live"
          ? `Messaggio inviato a ${res.results.length} contatti`
          : `WhatsApp non configurato: nessun messaggio inviato (${res.results.length} contatti in attesa)`
      );
    } catch {
      toast.error("Errore nell'invio");
    } finally {
      setSendingWA(false);
    }
  };

  const sendRecall = () => gate("whatsapp_message", doRecall, cohort.cohort.length);

  const doPreview = async () => {
    setGenerating(true);
    try {
      const res = await generateWeeklyReport(false, email);
      setPreview(res);
      setShowPreview(true);
    } catch {
      toast.error("Errore nella generazione");
    } finally {
      setGenerating(false);
    }
  };

  const doSend = async () => {
    setSending(true);
    try {
      const res = await generateWeeklyReport(true, email);
      if (res.delivery?.ok && res.delivery?.delivered_via === "smtp") {
        toast.success("Report inviato via email da Nadia Cloud");
      } else {
        toast.error(res.delivery?.error || "Report preparato ma non inviato: servizio email non disponibile");
      }
      setPreview(res);
    } catch {
      toast.error("Errore nell'invio");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {creditDialog}
      <PageHeader
        eyebrow="Automazioni"
        title="Messaggi e report di Nadia"
        subtitle="Prepara i follow-up WhatsApp e il report settimanale. Controlla l'anteprima e avvia l'invio quando vuoi."
      />

      {/* WhatsApp Recall */}
      <ProFeatureNotice access={planAccess} feature="whatsapp_automation" />
      <div className="nadia-card p-6 mb-6" data-testid="whatsapp-recall-card">
        <div className="flex items-start gap-4 mb-4">
          <span className="w-11 h-11 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-300 flex items-center justify-center shrink-0">
            <MessageCircle size={20} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading text-xl font-bold text-slate-50">Follow-up WhatsApp</h2>
              <span
                data-testid="whatsapp-config-status"
                className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider border ${
                  cohort.mode === "live"
                    ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/20"
                    : "bg-amber-500/10 text-amber-300 border-amber-500/20"
                }`}
              >
                {cohort.mode === "live" ? "Configurato" : "Non configurato"}
              </span>
            </div>
            <p className="text-sm text-slate-400 mt-1">
              Contatti elencati per il follow-up. La data di acquisizione non dimostra un clic sull'annuncio.
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4 mb-4">
          <div className="flex items-center gap-2 mb-3 text-xs text-slate-400 font-semibold uppercase tracking-wider">
            <Users size={12} /> Contatti elencati · {cohort.cohort.length}
          </div>
          <div className="space-y-2">
            {cohort.cohort.map((c) => (
              <div key={c.phone} className="flex flex-wrap items-start justify-between gap-2 text-sm" data-testid={`cohort-${c.phone}`}>
                <div className="min-w-0">
                  <span className="text-slate-100">{c.name}</span>
                  {/* Solo campi ricevuti: nessun mapping locale e nessun effetto sulla selezione. */}
                  <div className="mt-1 flex flex-wrap items-center gap-2 empty:hidden">
                    {c.status != null && (
                      <span className="text-xs text-slate-400" data-testid={`cohort-${c.phone}-original-status`}>
                        Stato originale: {String(c.status)}
                      </span>
                    )}
                    {typeof c.lead_status_normalized === "string" && c.lead_status_normalized.length > 0 && (
                      <Badge variant="outline" className="border-slate-500/30 text-slate-300" data-testid={`cohort-${c.phone}-normalized-status`}>
                        Stato normalizzato: {c.lead_status_normalized}
                      </Badge>
                    )}
                    {typeof c.lead_status_unresolved === "boolean" && (
                      <Badge variant="outline" className={c.lead_status_unresolved
                        ? "border-amber-500/30 bg-amber-500/10 text-amber-200"
                        : "border-slate-500/30 text-slate-300"} data-testid={`cohort-${c.phone}-unresolved-status`}>
                        {c.lead_status_unresolved ? "Alias non risolto" : "Alias risolto"}
                      </Badge>
                    )}
                  </div>
                </div>
                <span className="text-xs text-slate-500 font-mono" data-testid={`cohort-${c.phone}-acquired-at`}>
                  Acquisizione: {c.clicked_at} · +{c.phone}
                </span>
              </div>
            ))}
          </div>
        </div>

        <Button
          onClick={sendRecall}
          disabled={!planAccess.allows("whatsapp_automation") || sendingWA || cohort.cohort.length === 0}
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
          data-testid="send-whatsapp-recall-button"
        >
          {sendingWA ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Send size={15} className="mr-2" />}
          Invia messaggi ora
        </Button>

        {waResults && (
          <div className="mt-4 rounded-xl bg-emerald-500/5 border border-emerald-500/20 p-4">
            <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold mb-2">
              <CheckCircle2 size={15} /> Invii accettati ({waResults.results.filter((r) => r.ok).length})
            </div>
            <div className="text-xs text-slate-400 space-y-1">
              {waResults.results.map((r, i) => (
                <div key={i} className="font-mono truncate">
                  {r.recipient?.name} · {r.message_id || r.status || "ok"}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Weekly Report */}
      <ProFeatureNotice access={planAccess} feature="scheduled_reports" />
      <div className="nadia-card p-6" data-testid="weekly-report-card">
        <div className="flex items-start gap-4 mb-4">
          <span className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/25 text-cyan-300 flex items-center justify-center shrink-0">
            <Mail size={20} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-heading text-xl font-bold text-slate-50">Report settimanale</h2>
              <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-violet-500/10 text-violet-300 border border-violet-500/25">
                Scritto da Nadia
              </span>
            </div>
            <p className="text-sm text-slate-400 mt-1">
              Email personalizzata sui risultati disponibili, inviata direttamente dal servizio email di Nadia Cloud.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-2">
          <Input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="destinatario@esempio.it"
            className="bg-[#0B1120] border-white/10 h-11 text-slate-100"
            data-testid="report-email-input"
          />
          <div className="flex gap-2">
            <Button
              onClick={doPreview}
              disabled={generating}
              variant="outline"
              className="border-white/10 text-slate-200 hover:text-cyan-200 hover:border-cyan-400/50 bg-transparent"
              data-testid="preview-report-button"
            >
              {generating ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Eye size={15} className="mr-2" />}
              Anteprima
            </Button>
            <Button
              onClick={doSend}
              disabled={!planAccess.allows("scheduled_reports") || sending || !email}
              className="bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 hover:from-cyan-300 hover:to-violet-400 font-semibold"
              data-testid="send-report-button"
            >
              {sending ? <Loader2 size={15} className="mr-2 animate-spin" /> : <Send size={15} className="mr-2" />}
              Invia adesso
            </Button>
          </div>
        </div>

        {preview && (
          <div className="mt-5 rounded-xl bg-white/[0.02] border border-white/5 p-4">
            <div className="flex items-start gap-3">
              <NadiaAvatar size="sm" />
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-cyan-300 mb-1">Nadia ha scritto</div>
                <div className="font-heading text-base font-semibold text-slate-100">{preview.subject}</div>
                <p className="text-sm text-slate-300 mt-1 leading-relaxed">{preview.recap}</p>
                <p className="text-xs text-violet-300 mt-2">
                  <Zap size={11} className="inline mr-1" />
                  {preview.next}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-[#0B1120] border-white/10 text-slate-100 max-w-3xl max-h-[80vh] overflow-y-auto p-0">
          <DialogHeader className="p-5 border-b border-white/5">
            <DialogTitle className="font-heading text-lg">Anteprima email</DialogTitle>
          </DialogHeader>
          <div
            className="p-0"
            dangerouslySetInnerHTML={{ __html: preview?.html || "" }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
